/**
 * Shutter-capture risk assessment — scans the camera's exposed libgphoto2 config
 * tree for values that make physical-shutter capture slow or fragile in a booth.
 *
 * Why: with shutter capture ON, every shot is a full-size file transfer over the
 * single USB PTP channel before the next countdown can start. A "Large Fine JPEG"
 * (8–13 MB) takes many seconds on tethered PTP — guests experience a near-hang
 * between shots — and RAW/NEF (25–45 MB) is worse plus the pipeline can't process
 * it. The channel is also what liveview rides on, so nothing can hide the wait:
 * the ONLY real fix is a smaller on-camera file (Small/S1 Fine JPEG is the booth
 * sweet spot). This module just detects the risky values so the Camera Settings
 * modal can warn the operator right where they enable shutter capture.
 *
 * Widget-name coverage (per libgphoto2 drivers): Canon `imageformat` (+`...sd`/
 * `...cf`/`...exthd` card-slot variants) with values like "Large Fine JPEG" /
 * "RAW + Large Fine JPEG"; Nikon `imagequality` ("NEF (Raw)", "JPEG Fine") +
 * `imagesize` ("Large"/"Medium"/"Small"); Sony `imagequality`/`imagesize`.
 * Matching is by value text, so unknown models degrade to "no warning" rather
 * than a false alarm.
 */

export interface CameraConfigNodeLike {
  name: string
  label?: string
  type?: string
  value?: string | number | boolean
  children?: Record<string, CameraConfigNodeLike>
}

export type CaptureRiskLevel = 'high' | 'medium'

export interface CaptureRisk {
  level: CaptureRiskLevel
  /** Config widget name (e.g. 'imageformat') — used to build a jump-to anchor. */
  settingName: string
  /** Human label of the widget as the camera reports it. */
  settingLabel: string
  /** The current risky value. */
  value: string
  /** Short reason key: 'raw' | 'large' | 'medium' | 'card-target'. */
  reason: 'raw' | 'large' | 'medium' | 'card-target'
}

/** Widget names whose value describes the captured file's format/size. */
const IMAGE_FORMAT_WIDGET = /^(imageformat|imagequality|imagesize)/i

function flattenConfig(
  node: CameraConfigNodeLike | null | undefined,
  out: CameraConfigNodeLike[] = []
): CameraConfigNodeLike[] {
  if (!node) return out
  out.push(node)
  if (node.children) {
    for (const child of Object.values(node.children)) flattenConfig(child, out)
  }
  return out
}

function classifyImageValue(value: string): CaptureRisk['reason'] | null {
  // RAW in any combination (incl. "RAW + Large Fine JPEG", "NEF (Raw)", "cRAW").
  if (/\braw\b|\bnef\b|\bcraw\b/i.test(value)) return 'raw'
  // Explicit small/tiny formats are the recommended booth values — never warn.
  if (/small|tiny|\bs[123]?\b/i.test(value)) return null
  if (/large|\bl\b/i.test(value)) return 'large'
  if (/medium|\bm\b/i.test(value)) return 'medium'
  return null
}

export type CameraBrand = 'canon' | 'nikon' | 'sony' | 'unknown'

/**
 * Best-effort brand detection from the libgphoto2 config tree, used to pre-open the
 * matching section of the flash setup guide. Prefers the PTP DeviceInfo text widgets
 * (`manufacturer` / `cameramodel`); falls back to the Canon EOS driver's `eos*` widget
 * prefix. Unknown is a safe answer — the guide then just shows all brands collapsed.
 */
export function detectCameraBrandFromConfig(
  config: CameraConfigNodeLike | null | undefined
): CameraBrand {
  let sawEosWidget = false
  for (const node of flattenConfig(config)) {
    if (!node.name) continue
    if (/^(manufacturer|cameramodel|model)$/i.test(node.name) && typeof node.value === 'string') {
      const v = node.value.toLowerCase()
      if (v.includes('canon')) return 'canon'
      if (v.includes('nikon')) return 'nikon'
      if (v.includes('sony')) return 'sony'
    }
    if (/^eos/i.test(node.name)) sawEosWidget = true
  }
  return sawEosWidget ? 'canon' : 'unknown'
}

/**
 * Scan a camera config tree (the object CameraSettingsModal already holds) and
 * return the risks that make shutter capture slow. Empty array = nothing to warn
 * about (or the model exposes no recognizable format widgets — fail quiet).
 */
export function assessShutterCaptureRisks(
  config: CameraConfigNodeLike | null | undefined
): CaptureRisk[] {
  const risks: CaptureRisk[] = []
  for (const node of flattenConfig(config)) {
    if (typeof node.value !== 'string' || !node.name) continue

    if (IMAGE_FORMAT_WIDGET.test(node.name)) {
      const reason = classifyImageValue(node.value)
      if (reason) {
        risks.push({
          level: reason === 'raw' || reason === 'large' ? 'high' : 'medium',
          settingName: node.name,
          settingLabel: node.label || node.name,
          value: node.value,
          reason
        })
      }
      continue
    }

    // Capture target: writing to the memory card adds card-write latency to every
    // tethered shot; libgphoto2's "Internal RAM" is the fast tethering path.
    if (/^capturetarget$/i.test(node.name) && /card/i.test(node.value)) {
      risks.push({
        level: 'medium',
        settingName: node.name,
        settingLabel: node.label || node.name,
        value: node.value,
        reason: 'card-target'
      })
    }
  }
  return risks
}
